import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../../firebase/config';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc } from 'firebase/firestore';

const CANALES_DISPONIBLES = ['Agente', 'Pas', 'Operatoria', 'Directo'];
const FORMAS_LIQUIDACION = ['Comisión', 'Prima'];

const AdminComisiones = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('canales'); 
    const [loading, setLoading] = useState(true);
    
    const [canales, setCanales] = useState([]);
    const [subcanales, setSubcanales] = useState([]);
    const [companiasSincronizadas, setCompaniasSincronizadas] = useState([]);

    const [showModalCanal, setShowModalCanal] = useState(false);
    const [showModalSub, setShowModalSub] = useState(false);
    const [isEditing, setIsEditing] = useState(null);

    const [detailsModal, setDetailsModal] = useState({ show: false, title: '', data: [], type: '' });

    const canalesRef = collection(db, 'public/data/canales');
    const subcanalesRef = collection(db, 'public/data/subcanales');
    const companiasRef = collection(db, 'public/data/companias');

    const [formCanal, setFormCanal] = useState({
        nombre: '', 
        canal: 'Agente', 
        cuit: '',
        descuento: '',
        porcentajePatrimoniales: '',
        formaLiquidarPatrimoniales: '',
        formaLiquidarART: '',
        artEspeciales: [] 
    });

    const [formSub, setFormSub] = useState({
        nombre: '', 
        cuit: '',
        canalId: '', 
        patrimoniales: {
            distribucionCanal: '', 
            distribucionSubcanal: '',    
            descuentoCanal: '',    
            descuentoSubcanal: ''        
        },
        artReglas: [] 
    });

    const fetchData = async () => {
        setLoading(true);
        try {
            const canalSnap = await getDocs(canalesRef);
            const canalData = canalSnap.docs.map(doc => {
                const d = doc.data();
                return { 
                    id: doc.id, 
                    ...d,
                    nombre: d.nombre || d.subcanal, 
                    tipo: d.tipo || d.canal 
                };
            });
            canalData.sort((a, b) => a.tipo.localeCompare(b.tipo) || a.nombre.localeCompare(b.nombre));
            setCanales(canalData);

            const subSnap = await getDocs(subcanalesRef);
            const subData = subSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            
            const subEnriched = subData.map(s => {
                const parent = canalData.find(c => c.id === s.canalId);
                return { 
                    ...s, 
                    canalPadreNombre: parent ? parent.nombre : 'Desconocido', 
                    canalPadreTipo: parent ? parent.tipo : '-' 
                };
            });
            setSubcanales(subEnriched);

            const ciasSnap = await getDocs(companiasRef);
            const ciasData = ciasSnap.docs
                .map(d => d.data())
                .filter(c => c.grupo === 'ART')
                .map(c => c.nombre)
                .sort();
            setCompaniasSincronizadas(ciasData);

        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    // --- LÓGICA CANALES CON CONVERSIÓN NUMÉRICA ---
    const handleOpenModalCanal = (item = null) => {
        if (item) {
            setFormCanal({ 
                ...item, 
                tipo: item.tipo || item.canal, 
                artEspeciales: item.artEspeciales || [] 
            });
            setIsEditing(item.id);
        } else {
            setFormCanal({
                nombre: '', tipo: 'Agente', cuit: '', descuento: '',
                porcentajePatrimoniales: '', formaLiquidarPatrimoniales: '',
                formaLiquidarART: '', artEspeciales: []
            });
            setIsEditing(null);
        }
        setShowModalCanal(true);
    };

    const handleSaveCanal = async (e) => {
        e.preventDefault();
        try {
            // Conversión estricta a Number para campos y sub-arreglos
            const payload = { 
                ...formCanal, 
                porcentajePatrimoniales: Number(formCanal.porcentajePatrimoniales || 0), 
                descuento: Number(formCanal.descuento || 0),
                canal: formCanal.tipo || formCanal.canal, 
                nombre: formCanal.nombre,
                artEspeciales: formCanal.artEspeciales.map(art => ({
                    ...art,
                    porcentaje: Number(art.porcentaje || 0)
                }))
            };
            
            delete payload.subcanal; 

            if (isEditing) await updateDoc(doc(db, 'public/data/canales', isEditing), payload);
            else await addDoc(canalesRef, payload);
            
            setShowModalCanal(false);
            fetchData();
        } catch (error) { alert("Error al guardar canal"); }
    };

    const addArtCanal = () => setFormCanal({...formCanal, artEspeciales: [...formCanal.artEspeciales, { compania: '', porcentaje: '' }]});
    const removeArtCanal = (idx) => setFormCanal({...formCanal, artEspeciales: formCanal.artEspeciales.filter((_, i) => i !== idx)});
    const changeArtCanal = (idx, field, val) => { 
        const newArr = [...formCanal.artEspeciales]; 
        newArr[idx][field] = val; 
        setFormCanal({...formCanal, artEspeciales: newArr}); 
    };

    // --- LÓGICA SUBCANALES CON CONVERSIÓN NUMÉRICA ---
    const handleOpenModalSub = (item = null) => {
        if (item) {
            setFormSub({ 
                ...item, 
                canalId: item.canalId || item.organizadorId, 
                artReglas: item.artReglas || [] 
            });
            setIsEditing(item.id);
        } else {
            setFormSub({
                nombre: '', cuit: '', canalId: '',
                patrimoniales: { distribucionCanal: '', distribucionSubcanal: '', descuentoCanal: '', descuentoSubcanal: '' },
                artReglas: []
            });
            setIsEditing(null);
        }
        setShowModalSub(true);
    };

    const handleSaveSub = async (e) => {
        e.preventDefault();
        try {
            // Conversión estricta a Number para objetos anidados y arreglos
            const payload = { 
                ...formSub,
                patrimoniales: {
                    distribucionCanal: Number(formSub.patrimoniales.distribucionCanal || 0),
                    distribucionSubcanal: Number(formSub.patrimoniales.distribucionSubcanal || 0),
                    descuentoCanal: Number(formSub.patrimoniales.descuentoCanal || 0),
                    descuentoSubcanal: Number(formSub.patrimoniales.descuentoSubcanal || 0)
                },
                artReglas: formSub.artReglas.map(reg => ({
                    ...reg,
                    distCanal: Number(reg.distCanal || 0),
                    distSub: Number(reg.distSub || 0),
                    descCanal: Number(reg.descCanal || 0),
                    descSub: Number(reg.descSub || 0)
                }))
            };
            delete payload.organizadorId; 

            if (isEditing) await updateDoc(doc(db, 'public/data/subcanales', isEditing), payload);
            else await addDoc(subcanalesRef, payload);
            
            setShowModalSub(false);
            fetchData();
        } catch (error) { alert("Error al guardar subcanal"); }
    };

    const addArtSub = () => setFormSub({...formSub, artReglas: [...formSub.artReglas, { compania: '', distCanal: '', distSub: '', descCanal: '', descSub: '' }]});
    const removeArtSub = (idx) => setFormSub({...formSub, artReglas: formSub.artReglas.filter((_, i) => i !== idx)});
    const changeArtSub = (idx, field, val) => { 
        const newArr = [...formSub.artReglas]; 
        newArr[idx][field] = val; 
        setFormSub({...formSub, artReglas: newArr}); 
    };

    const handleDelete = async (collectionName, id) => {
        if (window.confirm("¿Seguro que deseas eliminar este registro?")) {
            await deleteDoc(doc(db, `public/data/${collectionName}`, id));
            fetchData();
        }
    };

    const handleChangeCanal = (e) => {
        const { name, value } = e.target;
        setFormCanal(prev => ({ ...prev, [name]: value }));
    };

    const handleChangeSub = (e) => {
        const { name, value } = e.target;
        setFormSub(prev => ({ ...prev, [name]: value }));
    };

    const openDetails = (title, data, type) => setDetailsModal({ show: true, title, data, type });

    return (
        <div className="p-8 bg-gray-50 min-h-screen font-sans text-gray-800">
            <div className="max-w-7xl mx-auto">
                
                <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4 text-left w-full">
                    <div className="w-full">
                        <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Gestión Comercial</h1>
                        <p className="text-gray-500 mt-1">Configuración de canales y subcanales.</p>
                    </div>
                    
                    <div className="flex bg-white p-1 rounded-xl shadow-sm border border-gray-200">
                        <button onClick={() => setActiveTab('canales')} className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === 'canales' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-500 hover:bg-gray-50'}`}>Canales</button>
                        <button onClick={() => setActiveTab('subcanales')} className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === 'subcanales' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-500 hover:bg-gray-50'}`}>Subcanales Asociados</button>
                    </div>

                    <button onClick={() => navigate('/AdminDashboard')} className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg font-bold shadow-sm hover:bg-gray-50 transition text-sm">Volver</button>
                </div>

                {activeTab === 'canales' && (
                    <>
                        <div className="flex justify-end mb-4">
                            <button onClick={() => handleOpenModalCanal()} className="bg-blue-600 text-white px-5 py-2.5 rounded-lg font-bold shadow hover:bg-blue-700 text-sm flex gap-2 items-center transition-all hover:scale-105">
                                + Nuevo Canal
                            </button>
                        </div>
                        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                            <table className="w-full text-left">
                                <thead className="bg-gray-50 text-gray-500 uppercase font-bold text-xs tracking-wider border-b border-gray-200">
                                    <tr>
                                        <th className="px-6 py-4 text-left">Canal</th>
                                        <th className="px-6 py-4 text-left">Nombre del Canal</th>
                                        <th className="px-6 py-4 text-left">CUIT</th>
                                        <th className="px-6 py-4 text-center">Desc. Gral.</th>
                                        <th className="px-6 py-4 text-left">Regla Patrimoniales</th>
                                        <th className="px-6 py-4 text-left">Regla ART</th>
                                        <th className="px-6 py-4 text-center">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 text-sm">
                                    {canales.map(org => (
                                        <tr key={org.id} className="hover:bg-blue-50/50 transition-colors">
                                            <td className="px-6 py-4 font-bold text-blue-800 text-left">{org.tipo}</td>
                                            <td className="px-6 py-4 font-medium text-gray-900 text-left">{org.nombre}</td>
                                            <td className="px-6 py-4 font-mono text-gray-500 text-left">{org.cuit || '-'}</td>
                                            <td className="px-6 py-4 text-center">
                                                {org.descuento > 0 ? <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded font-bold text-xs">-{org.descuento}%</span> : <span className="text-gray-300">-</span>}
                                            </td>
                                            <td className="px-6 py-4 text-left">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-bold text-green-700">{org.porcentajePatrimoniales}%</span>
                                                    <span className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded uppercase">{org.formaLiquidarPatrimoniales}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-left">
                                                <div className="flex items-center gap-2">
                                                    <div className="text-xs text-orange-800">
                                                        <span className="font-bold">{org.artEspeciales?.length || 0}</span> Reglas
                                                    </div>
                                                    <span className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded uppercase">{org.formaLiquidarART || '-'}</span>
                                                    {org.artEspeciales?.length > 0 && (
                                                        <button onClick={() => openDetails('Reglas ART por Compañía', org.artEspeciales, 'art_canal')} className="text-blue-500 hover:text-blue-700 p-1 rounded hover:bg-blue-50 transition">
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path></svg>
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <div className="flex justify-center gap-2">
                                                    <button onClick={() => handleOpenModalCanal(org)} className="text-gray-400 hover:text-blue-600 p-1"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536l12.232-12.232z"></path></svg></button>
                                                    <button onClick={() => handleDelete('canales', org.id)} className="text-gray-400 hover:text-red-600 p-1"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg></button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}

                {activeTab === 'subcanales' && (
                    <>
                        <div className="flex justify-end mb-4">
                            <button onClick={() => handleOpenModalSub()} className="bg-blue-600 text-white px-5 py-2.5 rounded-lg font-bold shadow hover:bg-blue-700 text-sm flex gap-2 items-center transition-all hover:scale-105">
                                + Nuevo Subcanal Asociado
                            </button>
                        </div>
                        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                            <table className="w-full text-left text-sm text-gray-600">
                                <thead className="bg-gray-50 text-gray-500 uppercase font-bold text-xs tracking-wider border-b border-gray-200">
                                    <tr>
                                        <th className="px-6 py-4 text-left">Nombre del Subcanal</th>
                                        <th className="px-6 py-4 text-left">CUIT</th>
                                        <th className="px-6 py-4 text-left">Canal Padre</th>
                                        <th className="px-6 py-4 text-left">Distribución Patrimoniales</th>
                                        <th className="px-6 py-4 text-left">Distribución ART</th>
                                        <th className="px-6 py-4 text-center">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {subcanales.map(ven => (
                                        <tr key={ven.id} className="hover:bg-blue-50/50 transition">
                                            <td className="px-6 py-4 font-bold text-gray-900 text-left">{ven.nombre}</td>
                                            <td className="px-6 py-4 font-mono text-xs text-gray-500 text-left">{ven.cuit || '-'}</td>
                                            <td className="px-6 py-4 text-left">
                                                <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs font-bold border border-blue-100">{ven.canalPadreNombre}</span>
                                            </td>
                                            <td className="px-6 py-4 text-left text-xs">
                                                <div className="flex flex-col gap-1.5">
                                                    <div className="flex items-center">
                                                        <span className="text-gray-500 w-12 text-left">Canal:</span> 
                                                        <b className="text-green-700 text-sm">{ven.patrimoniales?.distribucionCanal}%</b> 
                                                        {ven.patrimoniales?.descuentoCanal > 0 && <span className="ml-2 text-[10px] text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded font-bold">-{ven.patrimoniales?.descuentoCanal}%</span>}
                                                    </div>
                                                    <div className="flex items-center">
                                                        <span className="text-gray-500 w-12 text-left">Sub:</span> 
                                                        <b className="text-blue-700 text-sm">{ven.patrimoniales?.distribucionSubcanal}%</b>
                                                        {ven.patrimoniales?.descuentoSubcanal > 0 && <span className="ml-2 text-[10px] text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded font-bold">-{ven.patrimoniales?.descuentoSubcanal}%</span>}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-left">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs text-orange-800 font-medium">
                                                        {ven.artReglas?.length || 0} Reglas
                                                    </span>
                                                    {ven.artReglas?.length > 0 && (
                                                        <button onClick={() => openDetails('Distribución ART', ven.artReglas, 'art_sub')} className="text-blue-500 hover:text-blue-700 p-1 rounded hover:bg-blue-50 transition">
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path></svg>
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <div className="flex justify-center gap-2">
                                                    <button onClick={() => handleOpenModalSub(ven)} className="text-gray-400 hover:text-blue-600 p-1"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536l12.232-12.232z"></path></svg></button>
                                                    <button onClick={() => handleDelete('subcanales', ven.id)} className="text-gray-400 hover:text-red-600 p-1"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg></button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>

            {/* --- MODAL DETALLES --- */}
            {detailsModal.show && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-60 flex items-center justify-center p-4 z-[60] text-left">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md animate-fade-in overflow-hidden">
                        <div className="bg-gray-100 px-5 py-3 flex justify-between items-center border-b border-gray-200">
                            <h3 className="font-bold text-gray-800">{detailsModal.title}</h3>
                            <button onClick={() => setDetailsModal({ show: false, data: [] })} className="text-gray-400 hover:text-gray-600">✕</button>
                        </div>
                        <div className="p-5 max-h-[60vh] overflow-y-auto">
                            {detailsModal.type === 'art_canal' && (
                                <ul className="space-y-2">
                                    {detailsModal.data.map((item, i) => (
                                        <li key={i} className="flex justify-between items-center bg-gray-50 p-2 rounded border border-gray-100 text-sm">
                                            <span className="font-medium text-gray-700">{item.compania}</span>
                                            <span className="font-bold text-orange-600">{item.porcentaje}%</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {detailsModal.type === 'art_sub' && (
                                <ul className="space-y-2">
                                    {detailsModal.data.map((item, i) => (
                                        <li key={i} className="bg-gray-50 p-3 rounded border border-gray-100 text-xs text-left">
                                            <div className="font-bold text-gray-800 mb-2 border-b border-gray-200 pb-1">{item.compania}</div>
                                            <div className="flex flex-col gap-1">
                                                <div className="flex justify-between text-gray-600">
                                                    <span>Canal: <b>{item.distCanal}%</b> {item.descCanal > 0 && <span className="text-purple-500">(-{item.descCanal}%)</span>}</span>
                                                    <span>Sub: <b>{item.distSub}%</b> {item.descSub > 0 && <span className="text-purple-500">(-{item.descSub}%)</span>}</span>
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ================= MODAL CANAL ================= */}
            {showModalCanal && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-60 flex items-center justify-center p-4 z-50 text-left">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-fade-in max-h-[90vh] flex flex-col border border-gray-100">
                        <div className="bg-white px-6 py-4 flex justify-between items-center border-b border-gray-100">
                            <h3 className="font-bold text-xl text-gray-800">{isEditing ? 'Editar Canal' : 'Nuevo Canal'}</h3>
                            <button onClick={() => setShowModalCanal(false)} className="text-gray-400 hover:text-gray-600 transition">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                            </button>
                        </div>
                        <form onSubmit={handleSaveCanal} className="p-6 space-y-6 overflow-y-auto bg-gray-50">
                            <div className="grid grid-cols-2 gap-5">
                                <div>
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide text-left">Canal</label>
                                    <select name="tipo" value={formCanal.tipo} onChange={handleChangeCanal} className="w-full border border-gray-300 p-3 rounded-xl mt-1 bg-white focus:ring-2 focus:ring-blue-500 outline-none transition">
                                        {CANALES_DISPONIBLES.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide text-left">Nombre del Canal</label>
                                    <input type="text" name="nombre" value={formCanal.nombre} onChange={handleChangeCanal} className="w-full border border-gray-300 p-3 rounded-xl mt-1 font-medium focus:ring-2 focus:ring-blue-500 outline-none transition" required placeholder="Ej: Adrian Kurchin" />
                                </div>
                                <div className="col-span-2">
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide text-left">CUIT (Obligatorio)</label>
                                    <input type="text" name="cuit" value={formCanal.cuit} onChange={handleChangeCanal} className="w-full border border-gray-300 p-3 rounded-xl mt-1 focus:ring-2 focus:ring-blue-500 outline-none transition" required placeholder="Sin guiones" />
                                </div>
                            </div>

                            <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm text-left">
                                <h4 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                                    <span className="w-2 h-6 bg-green-500 rounded-full"></span>
                                    Configuración Patrimoniales
                                </h4>
                                <div className="space-y-4">
                                    <div>
                                        <label className="text-xs font-bold text-gray-500 block mb-1 text-left">Forma Liquidar</label>
                                        <select name="formaLiquidarPatrimoniales" value={formCanal.formaLiquidarPatrimoniales} onChange={handleChangeCanal} className="w-full border border-gray-300 p-2.5 rounded-xl bg-gray-50 focus:bg-white transition outline-none">
                                            <option value="">Seleccionar...</option>
                                            {FORMAS_LIQUIDACION.map(f => <option key={f} value={f}>{f}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="text-xs font-bold text-gray-500 block mb-1 text-left">Comisión Base (%)</label>
                                        <div className="relative">
                                             <input type="number" step="0.01" name="porcentajePatrimoniales" value={formCanal.porcentajePatrimoniales} onChange={handleChangeCanal} className="w-full border border-gray-300 p-2.5 rounded-xl pr-8 focus:ring-2 focus:ring-green-500 outline-none" placeholder="0" />
                                             <span className="absolute right-3 top-2.5 text-gray-400 font-bold">%</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm text-left">
                                <div className="flex justify-between items-center mb-4">
                                    <h4 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                                        <span className="w-2 h-6 bg-orange-500 rounded-full"></span>
                                        Configuración ART
                                    </h4>
                                    <button type="button" onClick={addArtCanal} className="text-xs bg-orange-50 text-orange-700 border border-orange-200 px-3 py-1.5 rounded-lg font-bold hover:bg-orange-100 transition">+ Regla Cía</button>
                                </div>
                                <div className="mb-4">
                                    <label className="text-xs font-bold text-gray-500 block mb-1 text-left">Forma Liquidar (Defecto)</label>
                                    <select name="formaLiquidarART" value={formCanal.formaLiquidarART} onChange={handleChangeCanal} className="w-full border border-gray-300 p-2.5 rounded-xl bg-gray-50 focus:bg-white transition outline-none">
                                        <option value="">Seleccionar...</option>
                                        {FORMAS_LIQUIDACION.map(f => <option key={f} value={f}>{f}</option>)}
                                    </select>
                                </div>
                                <div className="space-y-2 max-h-48 overflow-y-auto pr-2 custom-scrollbar text-left">
                                    {formCanal.artEspeciales.map((art, idx) => (
                                        <div key={idx} className="flex gap-2 items-center bg-gray-50 p-2 rounded-lg border border-gray-100">
                                            <select value={art.compania} onChange={(e) => changeArtCanal(idx, 'compania', e.target.value)} className="flex-grow bg-white border border-gray-200 rounded-lg p-2 text-sm outline-none">
                                                <option value="">Cía...</option>
                                                {companiasSincronizadas.map(c => <option key={c} value={c}>{c}</option>)}
                                            </select>
                                            <div className="relative w-24">
                                                <input type="number" step="0.01" value={art.porcentaje} onChange={(e) => changeArtCanal(idx, 'porcentaje', e.target.value)} className="w-full border border-gray-200 rounded-lg p-2 text-sm pr-6 text-center font-bold" placeholder="0" />
                                                <span className="absolute right-2 top-2 text-gray-400 text-xs font-bold">%</span>
                                            </div>
                                            <button type="button" onClick={() => removeArtCanal(idx)} className="text-gray-400 hover:text-red-500 p-1 transition">✕</button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="bg-purple-50 p-4 rounded-2xl border border-purple-200 shadow-sm flex items-center gap-4 mt-4 text-left">
                                <div className="p-3 bg-white rounded-full text-purple-600 shadow-sm">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"></path></svg>
                                </div>
                                <div className="flex-grow">
                                    <label className="block text-sm font-bold text-purple-900 mb-1 text-left">Descuento General</label>
                                    <p className="text-xs text-purple-700 opacity-80 text-left">Aplica a todas las operaciones de este canal.</p>
                                </div>
                                <div className="relative w-32">
                                    <input 
                                        type="number" 
                                        step="0.01"
                                        name="descuento" 
                                        value={formCanal.descuento} 
                                        onChange={handleChangeCanal} 
                                        className="w-full border border-purple-200 rounded-xl p-3 pr-8 outline-none focus:border-purple-500 text-right font-bold text-purple-900 bg-white shadow-sm" 
                                        placeholder="0" 
                                    />
                                    <span className="absolute right-3 top-3 text-purple-400 font-bold">%</span>
                                </div>
                            </div>

                            <button type="submit" className="w-full bg-blue-600 text-white py-3.5 rounded-xl font-bold hover:bg-blue-700 shadow-lg transition transform hover:scale-[1.01]">Guardar Canal</button>
                        </form>
                    </div>
                </div>
            )}

            {/* ================= MODAL SUBCANAL ================= */}
            {showModalSub && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-60 flex items-center justify-center p-4 z-50 text-left">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden animate-fade-in max-h-[90vh] flex flex-col border border-gray-100">
                        <div className="bg-white px-6 py-4 flex justify-between items-center border-b border-gray-100">
                            <h3 className="font-bold text-xl text-gray-800">{isEditing ? 'Editar Subcanal Asociado' : 'Nuevo Subcanal Asociado'}</h3>
                            <button onClick={() => setShowModalSub(false)} className="text-gray-400 hover:text-gray-600 transition">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                            </button>
                        </div>
                        <form onSubmit={handleSaveSub} className="p-6 space-y-6 overflow-y-auto bg-gray-50">
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                                <div className="md:col-span-1">
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide text-left">Canal Padre *</label>
                                    <select 
                                        name="canalId"
                                        value={formSub.canalId} 
                                        onChange={handleChangeSub} 
                                        className="w-full border border-blue-200 bg-white p-3 rounded-xl mt-1 focus:ring-2 focus:ring-blue-500 outline-none transition" 
                                        required
                                    >
                                        <option value="">Seleccione...</option>
                                        {canales.map(o => <option key={o.id} value={o.id}>{o.nombre} ({o.tipo})</option>)}
                                    </select>
                                </div>
                                <div className="md:col-span-1">
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide text-left">Nombre del Subcanal</label>
                                    <input type="text" name="nombre" value={formSub.nombre} onChange={handleChangeSub} className="w-full border border-gray-300 p-3 rounded-xl mt-1 font-medium focus:ring-2 focus:ring-blue-500 outline-none" required />
                                </div>
                                <div className="md:col-span-1">
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide text-left">CUIT (Obligatorio)</label>
                                    <input type="text" name="cuit" value={formSub.cuit} onChange={handleChangeSub} className="w-full border border-gray-300 p-3 rounded-xl mt-1 focus:ring-2 focus:ring-blue-500 outline-none" required />
                                </div>
                            </div>

                            <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm text-left">
                                <h4 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                                    <span className="w-2 h-6 bg-green-500 rounded-full"></span>
                                    Distribución Patrimoniales
                                </h4>
                                <div className="grid grid-cols-2 gap-8">
                                    <div className="space-y-3">
                                        <h5 className="text-xs font-bold text-gray-500 uppercase text-center border-b pb-1">Canal (Padre)</h5>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="text-[10px] text-gray-500 block mb-1 text-left">% Com</label>
                                                <input type="number" step="0.01" value={formSub.patrimoniales.distribucionCanal} onChange={(e) => setFormSub({...formSub, patrimoniales: {...formSub.patrimoniales, distribucionCanal: e.target.value}})} className="w-full border border-gray-300 p-2 rounded-lg text-center focus:border-green-500 outline-none" />
                                            </div>
                                            <div>
                                                <label className="text-[10px] text-gray-500 block mb-1 text-left">Desc %</label>
                                                <input type="number" step="0.01" value={formSub.patrimoniales.descuentoCanal} onChange={(e) => setFormSub({...formSub, patrimoniales: {...formSub.patrimoniales, descuentoCanal: e.target.value}})} className="w-full border border-purple-200 bg-purple-50 text-purple-900 p-2 rounded-lg text-center focus:border-purple-500 outline-none" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="space-y-3 border-l border-gray-100 pl-8">
                                        <h5 className="text-xs font-bold text-gray-500 uppercase text-center border-b pb-1">Subcanal (Este)</h5>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="text-[10px] text-gray-500 block mb-1 text-left">% Com</label>
                                                <input type="number" step="0.01" value={formSub.patrimoniales.distribucionSubcanal} onChange={(e) => setFormSub({...formSub, patrimoniales: {...formSub.patrimoniales, distribucionSubcanal: e.target.value}})} className="w-full border border-gray-300 p-2 rounded-lg text-center focus:border-blue-500 outline-none" />
                                            </div>
                                            <div>
                                                <label className="text-[10px] text-gray-500 block mb-1 text-left">Desc %</label>
                                                <input type="number" step="0.01" value={formSub.patrimoniales.descuentoSubcanal} onChange={(e) => setFormSub({...formSub, patrimoniales: {...formSub.patrimoniales, descuentoSubcanal: e.target.value}})} className="w-full border border-purple-200 bg-purple-50 text-purple-900 p-2 rounded-lg text-center focus:border-purple-500 outline-none" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm text-left">
                                <div className="flex justify-between items-center mb-4">
                                    <h4 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                                        <span className="w-2 h-6 bg-orange-500 rounded-full"></span>
                                        Distribución ART (Por Compañía)
                                    </h4>
                                    <button type="button" onClick={addArtSub} className="text-xs bg-orange-50 text-orange-700 border border-orange-200 px-3 py-1.5 rounded-lg font-bold hover:bg-orange-100 transition shadow-sm">
                                        + Agregar Regla
                                    </button>
                                </div>
                                
                                {formSub.artReglas.length === 0 && <p className="text-xs text-gray-400 italic text-center py-6 bg-gray-50 rounded-lg border border-dashed border-gray-200">Sin reglas específicas.</p>}

                                <div className="space-y-3 max-h-64 overflow-y-auto custom-scrollbar pr-2 text-left">
                                    {formSub.artReglas.map((regla, idx) => (
                                        <div key={idx} className="bg-gray-50 p-4 rounded-xl border border-gray-200 shadow-sm">
                                            <div className="flex justify-between mb-3">
                                                <select value={regla.compania} onChange={(e) => changeArtSub(idx, 'compania', e.target.value)} className="border border-gray-300 rounded-lg text-sm p-2 w-1/2 font-medium bg-white focus:ring-2 focus:ring-orange-200 outline-none">
                                                    <option value="">Seleccionar Cía...</option>
                                                    {companiasSincronizadas.map(c => <option key={c} value={c}>{c}</option>)}
                                                </select>
                                                <button type="button" onClick={() => removeArtSub(idx)} className="text-red-400 hover:text-red-600 hover:bg-red-50 p-2 rounded transition text-xs font-bold">Eliminar</button>
                                            </div>
                                            
                                            <div className="grid grid-cols-2 gap-8">
                                                <div className="grid grid-cols-2 gap-2">
                                                    <div><label className="text-[9px] text-gray-500 block uppercase text-left">Canal %</label><input type="number" step="0.01" value={regla.distCanal} onChange={(e) => changeArtSub(idx, 'distCanal', e.target.value)} className="w-full border p-1.5 rounded text-sm text-center" /></div>
                                                    <div><label className="text-[9px] text-gray-500 block uppercase text-left">Desc %</label><input type="number" step="0.01" value={regla.descCanal} onChange={(e) => changeArtSub(idx, 'descCanal', e.target.value)} className="w-full border p-1.5 rounded text-sm text-center bg-purple-50 text-purple-900 border-purple-200" /></div>
                                                </div>
                                                <div className="grid grid-cols-2 gap-2 border-l border-gray-300 pl-4">
                                                    <div><label className="text-[9px] text-gray-500 block uppercase text-left">Sub %</label><input type="number" step="0.01" value={regla.distSub} onChange={(e) => changeArtSub(idx, 'distSub', e.target.value)} className="w-full border p-1.5 rounded text-sm text-center" /></div>
                                                    <div><label className="text-[9px] text-gray-500 block uppercase text-left">Desc %</label><input type="number" step="0.01" value={regla.descSub} onChange={(e) => changeArtSub(idx, 'descSub', e.target.value)} className="w-full border p-1.5 rounded text-sm text-center bg-purple-50 text-purple-900 border-purple-200" /></div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <button type="submit" className="w-full bg-blue-600 text-white py-3.5 rounded-xl font-bold hover:bg-blue-700 shadow-lg transition transform hover:scale-[1.01]">Guardar Subcanal Asociado</button>
                        </form>
                    </div>
                </div>
            )}

        </div>
    );
};

export default AdminComisiones;